import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonJs } from '../common/common-js';
import { Router } from '@angular/router';
import { VaniToasterService } from 'src/app/services/vani-toaster.service';
import * as _ from "lodash";
import { GridService } from '../services/grid.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AuthService } from '../services/auth/auth.service';
import { environment } from '../../environments/environment';
import { TenantService } from '../services/tenant.service';
import * as moment from 'moment-timezone';

@Injectable()
export class CustomInterceptor implements HttpInterceptor {
    private activeRequests = 0;
    bsModalRef: BsModalRef;
    private isAccountExpiredHandled = false;

    constructor(
        private spinner: NgxSpinnerService,
        private modalService: BsModalService,
        public commonJs: CommonJs,
        private router: Router,
        private toastrService: VaniToasterService,
        public authService: AuthService,
        private tenantService: TenantService,
    ) {}

    // === Canonical timezone resolver inside same file ===
    private getCanonicalTimeZone(tz: string): string {
        const aliasMap: { [key: string]: string } = {
            'Asia/Calcutta': 'Asia/Kolkata',
            'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
            'US/Eastern': 'America/New_York',
            'US/Central': 'America/Chicago',
            'US/Mountain': 'America/Denver',
            'US/Pacific': 'America/Los_Angeles',
            // Add more aliases if needed
        };
        return aliasMap[tz] || tz;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Determine if the request requires 'Access-Code' header or not
        const requiresAccessCodeHeader = !this.isLoginRequest(request);

        // Detarmine if the request requires from project level
        const requestFromProjectLevel = this.checkIsProjectAPicall();

        // Get tenant ID either from localStorage or extract from URL if available
        let tenantId = localStorage.getItem('access_code');
        if (!tenantId || tenantId === '') {
            // First try to extract from subdomain
            const hostname = window.location.hostname;
            if (environment.tenancyConfig.type === 'subdomain' &&
                hostname.includes('.') &&
                !hostname.startsWith('www.')) {
                // Extract subdomain (the part before the first dot)
                tenantId = hostname.split('.')[0];
                if (tenantId) {
                    // Store for future use
                    localStorage.setItem('access_code', tenantId);
                }
            }

            // If still not found, try to extract from URL path
            if (!tenantId || tenantId === '') {
                // Try to extract from URL if not in localStorage
                const urlParts = this.router.url.split('/');
                const acIndex = urlParts.findIndex(part => part === 'ac');
                if (acIndex !== -1 && acIndex + 1 < urlParts.length) {
                    tenantId = urlParts[acIndex + 1];
                    // Store for future use
                    localStorage.setItem('access_code', tenantId);
                }
            }
        }

        // Use moment.tz.guess() and convert to canonical timezone name here
        const canonicalTimeZone = this.getCanonicalTimeZone(moment.tz.guess());

        const authRequest = request.clone({
            setHeaders: {
                ...(!request.url.includes('login') && { Authorization: 'Bearer ' + localStorage.getItem('token') }),
                TZ: canonicalTimeZone,
                'X-Tenant': tenantId || '',
                ...((!request.url.includes('commentStore') && !request.url.includes('addScripts')) && { 'Content-Type': 'application/json' }),
                // Keep existing header for backward compatibility
                ...(requiresAccessCodeHeader && { 'Access-Code': tenantId || '' }),
                ...(requestFromProjectLevel && { 'pAccess-Code': requestFromProjectLevel }),
            }
        });
        // Modify the request or perform any other actions here
        // For example, you can add headers or authentication tokens
        // Clone the request and modify it
        // Pass the modified request to the next interceptor or the HTTP handler
        let authCheck = localStorage.getItem('role');
        return next.handle(authRequest).pipe(
            finalize(() => {}),
            tap(() => {
                this.modalService.hide(777);
            }),
            catchError((error: HttpErrorResponse) => {
                console.log('Interceptor Error Details:', {
                    status: error.status,
                    statusText: error.statusText,
                    error: error.error,
                    message: error.message,
                    name: error.name,
                    url: error.url,
                    type: error.type,
                    headers: error.headers,
                    ok: error.ok
                });

                // Handle tenant not found errors
                if (error.status === 404 && error.error && error.error.code === 'tenant_not_found') {
                    this.tenantService.handleTenantNotFoundError(error);
                }

                // Handle expired account (499) only once
                if (error.error && error.error.code === 499) {
                    if (!this.toastrService.isAccountExpiredHandled) {
                        this.toastrService.notify('error', '', error.error.message || 'It seems your account is expired. Please contact support.');
                        localStorage.clear();
                        this.router.navigate(['/login']);
                    }
                    // Always return an empty observable to cancel further processing
                    return throwError(() => error);
                }

                // Continue with existing error handling logic
                this.modalService.hide(777);

                // Handle unauthorized errors
                if (error.status === 401) {
                    if (authCheck !== 'superadmin') {
                        this.logoutAccount();
                    }
                }
                return throwError(() => error);
            })
        );
    }

    private isLoginRequest(request: HttpRequest<any>): boolean {
        return request.url.includes('login');
    }

    private checkIsProjectAPicall() {
        const projectHeader = _.includes(this.router.url, 'projects');
        if (projectHeader) {
            const routerURL = this.router.url.split("/");
            const index = routerURL.findIndex(col => col == 'projects');
            return routerURL[index + 2];
        }
        return null;
    }

    logoutAccount() {
        this.spinner.show();
        localStorage.setItem('userId', '');
        localStorage.setItem('user', '');
        localStorage.removeItem('currentTask');
        localStorage.removeItem('projectRoles');
        localStorage.removeItem('projectRoleType');
        localStorage.removeItem('projectPermissions');
        localStorage.setItem('roles', JSON.stringify({}));
        localStorage.setItem('role', '');
        localStorage.setItem('standalone', undefined);
        localStorage.setItem('token', '');
        localStorage.setItem('access_code', '');
        let client_code = localStorage.getItem('client_code');
        this.router.navigate(['/login']);
        this.spinner.hide();
    }
}
